/**
 * Wallet Service
 * API service for student rewards wallet operations
 */

import { supabase } from './supabaseClient';
import { getApiBaseUrl } from './config/apiConfig';

const API_BASE_URL = `${getApiBaseUrl()}/api`;

/**
 * Get authentication token from current session
 * @returns {Promise<string|null>} - JWT token or null
 */
async function getAuthToken() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}

/**
 * Get student's wallet items
 * @param {string} status - Comma-separated statuses (e.g., "unclaimed,claimed,used")
 * @returns {Promise<{data: Array|null, error: string|null}>}
 */
export async function getWalletItems(status = 'unclaimed,claimed,used') {
  try {
    const token = await getAuthToken();
    if (!token) {
      return { data: null, error: 'Not authenticated' };
    }

    const response = await fetch(`${API_BASE_URL}/wallet/items?status=${status}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      let errorMessage = 'Failed to fetch wallet items';
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch (e) {
        // If response is not JSON, use status text
        errorMessage = response.statusText || errorMessage;
      }
      return { data: null, error: errorMessage };
    }

    const result = await response.json();
    return { data: result.items, error: null };
  } catch (error) {
    console.error('getWalletItems error:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Claim an unclaimed reward (generates coupon code)
 * @param {number} walletItemId - ID of the wallet item to claim
 * @returns {Promise<{data: Object|null, error: string|null}>}
 */
export async function claimReward(walletItemId) {
  try {
    const token = await getAuthToken();
    if (!token) {
      return { data: null, error: 'Not authenticated' };
    }

    const response = await fetch(`${API_BASE_URL}/wallet/claim/${walletItemId}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      let errorMessage = 'Failed to claim reward';
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch (e) {
        // If response is not JSON, use status text
        errorMessage = response.statusText || errorMessage;
      }
      return { data: null, error: errorMessage };
    }

    const result = await response.json();
    return { data: result.item, error: null };
  } catch (error) {
    console.error('claimReward error:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Get available coupons for booking checkout (claimed but unused)
 * @returns {Promise<{data: Array|null, error: string|null}>}
 */
export async function getAvailableCoupons() {
  try {
    const token = await getAuthToken();
    if (!token) {
      return { data: null, error: 'Not authenticated' };
    }

    const response = await fetch(`${API_BASE_URL}/wallet/available-coupons`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const error = await response.json();
      return { data: null, error: error.error || 'Failed to fetch available coupons' };
    }

    const result = await response.json();
    return { data: result.coupons, error: null };
  } catch (error) {
    console.error('getAvailableCoupons error:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Validate coupon code for a booking
 * @param {string} couponCode - Coupon code to validate
 * @param {number} bookingTotal - Total booking price
 * @returns {Promise<{data: Object|null, error: string|null}>}
 * Response: { valid, discount_amount, new_total, reward_label }
 */
export async function validateCoupon(couponCode, bookingTotal) {
  try {
    const token = await getAuthToken();
    if (!token) {
      return { data: null, error: 'Not authenticated' };
    }

    const response = await fetch(`${API_BASE_URL}/booking/validate-coupon`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ coupon_code: couponCode, booking_total: bookingTotal })
    });

    if (!response.ok) {
      const error = await response.json();
      return { data: null, error: error.error || 'Failed to validate coupon' };
    }

    const result = await response.json();
    return { data: result, error: null };
  } catch (error) {
    console.error('validateCoupon error:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Mark coupon as used (called after booking creation)
 * @param {string} couponCode - Coupon code
 * @param {number} bookingId - Booking ID
 * @returns {Promise<{data: Object|null, error: string|null}>}
 */
export async function markCouponAsUsed(couponCode, bookingId) {
  try {
    const token = await getAuthToken();
    if (!token) {
      return { data: null, error: 'Not authenticated' };
    }

    const response = await fetch(`${API_BASE_URL}/wallet/use-coupon`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ coupon_code: couponCode, booking_id: bookingId })
    });

    if (!response.ok) {
      const error = await response.json();
      return { data: null, error: error.error || 'Failed to mark coupon as used' };
    }

    const result = await response.json();
    return { data: result, error: null };
  } catch (error) {
    console.error('markCouponAsUsed error:', error);
    return { data: null, error: error.message };
  }
}
